import { useCallback, useEffect } from "react";
import { SubscriptionData } from "../../api/useWebSockets";
import { getRecords } from "../../api/useRecords";
import { getStore } from "../../store/store";
import { useUser } from "../../api/useAuth";
import { useUserTodayClinic } from "../../api/useUserTodayClinic";
import { formatClientName, isClinicReceptionist, isInternalStaff, addTaskToAssistant } from "./utils";
import { SessionUpdateData, TaskUpdateData, SessionInfo } from "./types";
import { isNfbRelatedRole } from "../../utils/rolesUtils";
import { SPECIAL_USERS } from "../../utils/constants";

const ONLINE_SERVICE_IDS = [37, 40, 50];

const SESSION_FIELDS = [
    "id",
    "status",
    "service",
    "technician.user_id",
    "treatment.id",
    "treatment.client.id",
    "treatment.client.client_id",
    "treatment.client.user_id.first_name",
    "treatment.client.user_id.last_name",
    "treatment.client.clinic_id.id",
    "treatment.client.clinic_id.receptionist",
].join(",");

const isOnlineService = (serviceId: number | undefined) => {
    if (!serviceId) return false;
    const isOnline = ONLINE_SERVICE_IDS.includes(serviceId);
    return isOnline;
};

export const useSessionAssistant = () => {
    const { data: currentUser } = useUser();
    const userTodayClinic = useUserTodayClinic({});
    const userTodayClinicKey = String(userTodayClinic);

    // Fetch tasks assigned to current user OR, for receptionists, the clinic's reception account
    const fetchOpenTasksForTreatment = async (sessionInfo: SessionInfo, assignedUserIds: Array<string>) => {
        const { items: tasks } = await getRecords("tasks", "*.*,related_with.item:treatments.*", {
            extraOptions: {
                filter: {
                    _and: [
                        { status: { _eq: "open" } },
                        { assigned_users: { directus_users_id: { id: { _in: assignedUserIds } } } },
                        {
                            related_with: {
                                "item:treatments": {
                                    id: { _eq: sessionInfo.treatmentId },
                                },
                            },
                        },
                    ],
                },
            },
        });

        tasks.forEach((task: any) => addTaskToAssistant(task, sessionInfo));
    };

    // Fetch tasks assigned to current user OR the NFB team (SPECIAL_USERS.nfb_team)
    const fetchTasksForNfbTeam = async (sessionInfo: SessionInfo, userId: string) => {
        const { items: tasks } = await getRecords("tasks", "*.*,related_with.item:treatments.*", {
            extraOptions: {
                filter: {
                    _and: [
                        { status: { _eq: "open" } },
                        {
                            _or: [
                                { assigned_users: { directus_users_id: { id: { _eq: userId } } } },
                                { assigned_users: { directus_users_id: { id: { _eq: SPECIAL_USERS.nfb_team } } } },
                            ],
                        },
                        {
                            related_with: {
                                "item:treatments": {
                                    id: { _eq: sessionInfo.treatmentId },
                                },
                            },
                        },
                    ],
                },
            },
        });

        tasks.forEach((task: any) => addTaskToAssistant(task, sessionInfo));
    };

    const handleSessionEvent = useCallback(
        (subscriptionData: SubscriptionData) => {
            const { event, data, uid } = subscriptionData;

            if (event !== "update" || !data || !Array.isArray(data)) return;

            const { assistantDialog } = getStore();
            const userRole = currentUser?.role?.id;

            if (uid === "sessions") {
                data.forEach((session: SessionUpdateData) => {
                    const clientData = session.treatment?.client;
                    const clientName = formatClientName(clientData);

                    // Handle "awaiting_in_clinic" for receptionists
                    if (session.status === "awaiting_in_clinic") {
                        const isReceptionist = isClinicReceptionist(clientData, currentUser, userTodayClinic);
                        const isJoanaOliveira = currentUser?.id === SPECIAL_USERS.joana_director;
                        if (!isReceptionist && !isJoanaOliveira) return;
                        if (isInternalStaff(clientData)) return;
                        if (isOnlineService(session.service)) return;

                        if (session.treatment?.id && currentUser?.id) {
                            const receptionAccountId = clientData?.clinic_id?.receptionist;
                            const assignedUserIds = isReceptionist && receptionAccountId ? [currentUser.id, receptionAccountId] : [currentUser.id];
                            assistantDialog.removeItemsBySessionId(session.id);
                            fetchOpenTasksForTreatment({
                                sessionId: session.id,
                                treatmentId: session.treatment.id,
                                clientName: clientName,
                            }, assignedUserIds);
                        }
                    }

                    // Handle "in_session" for NFB-related roles (client_owner, nfb_techncians, nfb_and_theurapist)
                    // Only show tasks to the technician assigned to this session
                    if (session.status === "in_session") {
                        if (!isNfbRelatedRole(userRole)) return;
                        if (session.technician?.user_id !== currentUser?.id) return;
                        if (isInternalStaff(clientData)) return;
                        if (isOnlineService(session.service)) return;
            
                        if (session.treatment?.id && currentUser?.id) {
                            assistantDialog.removeItemsBySessionId(session.id);
                            fetchTasksForNfbTeam({
                                sessionId: session.id,
                                treatmentId: session.treatment.id,
                                clientName: clientName,
                            }, currentUser.id);
                        }
                    }

                    const cancelledStatuses = ["done", "missed", "cancelled", "staff_unavailable"];
                    if (cancelledStatuses.includes(session.status)) {
                        assistantDialog.removeItemsBySessionId(session.id);
                    }
                });
            }

            if (uid === "tasks") {
                data.forEach((task: TaskUpdateData) => {
                    if (task.status === "done") {
                        const itemsToRemove = assistantDialog.items.filter(
                            (item) => item.id.startsWith(`task-${task.id}-`)
                        );
                        itemsToRemove.forEach((item) => assistantDialog.removeItem(item.id));
                    }
                });
            }
        },
        [currentUser, userTodayClinicKey]
    );

    // Initial check on load for existing sessions
    useEffect(() => {
        if (!currentUser?.id || userTodayClinic === undefined) return;

        const userRole = currentUser?.role?.id;

        const checkExistingSessions = async () => {
            const { items: sessions } = await getRecords(
                "sessions",
                SESSION_FIELDS,
                {
                    extraOptions: {
                        filter: {
                            _and: [
                                { status: { _in: ["awaiting_in_clinic", "in_session"] } },
                                { service: { _nnull: true } },
                            ],
                        },
                    },
                }
            );

            // Separate sessions by status for different handling logic
            const awaitingSessionsForReceptionist: Array<SessionInfo> = [];
            const awaitingTreatmentIds: Array<number> = [];
            const awaitingReceptionAccountIds: Array<string> = [];

            const inSessionForNfb: Array<SessionInfo> = [];
            const inSessionTreatmentIds: Array<number> = [];

            sessions.forEach((session: any) => {
                const clientData = session.treatment?.client;
                if (isInternalStaff(clientData)) return;
                if (isOnlineService(session.service)) return;
                if (!session.treatment?.id) return;

                const sessionInfo: SessionInfo = {
                    sessionId: session.id,
                    treatmentId: session.treatment.id,
                    clientName: formatClientName(clientData),
                };

                // "awaiting_in_clinic" sessions - for receptionists
                if (session.status === "awaiting_in_clinic") {
                    const isReceptionist = isClinicReceptionist(clientData, currentUser, userTodayClinic);
                    const isJoanaOliveira = currentUser?.id === SPECIAL_USERS.joana_director;
                    if (isReceptionist || isJoanaOliveira) {
                        awaitingSessionsForReceptionist.push(sessionInfo);
                        awaitingTreatmentIds.push(session.treatment.id);
                    }
                    const receptionAccountId = clientData?.clinic_id?.receptionist;
                    if (isReceptionist && receptionAccountId && !awaitingReceptionAccountIds.includes(receptionAccountId)) {
                        awaitingReceptionAccountIds.push(receptionAccountId);
                    }
                }

                // "in_session" sessions - for NFB-related roles (only for the assigned technician)
                if (session.status === "in_session") {
                    const isSessionTechnician = session.technician?.user_id === currentUser?.id;
                    if (isNfbRelatedRole(userRole) && isSessionTechnician) {
                        inSessionForNfb.push(sessionInfo);
                        inSessionTreatmentIds.push(session.treatment.id);
                    }
                }
            });

            // Fetch tasks for awaiting sessions (assigned to current user OR the clinic's reception account)
            if (awaitingTreatmentIds.length > 0) {
                const { items: tasks } = await getRecords("tasks", "*.*,related_with.item:treatments.*", {
                    extraOptions: {
                        filter: {
                            _and: [
                                { status: { _eq: "open" } },
                                { assigned_users: { directus_users_id: { id: { _in: [currentUser.id, ...awaitingReceptionAccountIds] } } } },
                                {
                                    related_with: {
                                        "item:treatments": {
                                            id: { _in: awaitingTreatmentIds },
                                        },
                                    },
                                },
                            ],
                        },
                    },
                });

                tasks.forEach((task: any) => {
                    const taskTreatmentId = task.related_with?.find((r: any) => r.collection === "treatments")?.item?.id;
                    const sessionInfo = awaitingSessionsForReceptionist.find((s) => s.treatmentId === taskTreatmentId);
                    if (!sessionInfo) return;
                    addTaskToAssistant(task, sessionInfo);
                });
            }

            // Fetch tasks for in_session (assigned to current user OR nfb_team)
            if (inSessionTreatmentIds.length > 0) {
                const { items: tasks } = await getRecords("tasks", "*.*,related_with.item:treatments.*", {
                    extraOptions: {
                        filter: {
                            _and: [
                                { status: { _eq: "open" } },
                                {
                                    _or: [
                                        { assigned_users: { directus_users_id: { id: { _eq: currentUser.id } } } },
                                        { assigned_users: { directus_users_id: { id: { _eq: SPECIAL_USERS.nfb_team } } } },
                                    ],
                                },
                                {
                                    related_with: {
                                        "item:treatments": {
                                            id: { _in: inSessionTreatmentIds },
                                        },
                                    },
                                },
                            ],
                        },
                    },
                });

                tasks.forEach((task: any) => {
                    const taskTreatmentId = task.related_with?.find((r: any) => r.collection === "treatments")?.item?.id;
                    const sessionInfo = inSessionForNfb.find((s) => s.treatmentId === taskTreatmentId);
                    if (!sessionInfo) return;
                    addTaskToAssistant(task, sessionInfo);
                });
            }
        };

        checkExistingSessions();
    }, [currentUser?.id, userTodayClinicKey]);

    return { handleSessionEvent };
};

